import { log } from "@soulbyte/core";
import { z } from "zod";

/**
 * Prefix for `useAuthGroups` query keys. Hooks append `{ tenantId }`; invalidate
 * with this prefix so every tenant cache is cleared after mutations.
 */
export const AUTH_GROUPS_QUERY_KEY = [
  "frontegg",
  "identity",
  "groups",
] as const;

/** User embedded in a Frontegg group (`_groupsRelations=rolesAndUsers`). */
const fronteggGroupUserSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    email: z.string().optional(),
    createdAt: z.string().optional(),
    profilePictureUrl: z.string().optional(),
    activatedForTenant: z.boolean().optional(),
  })
  .passthrough();

export type FronteggGroupUser = z.infer<typeof fronteggGroupUserSchema>;

/** Role embedded on a Frontegg group (`_groupsRelations=roles` / `rolesAndUsers`). */
const fronteggGroupRoleSchema = z
  .object({
    id: z.string(),
    key: z.string().optional(),
    name: z.string().optional(),
  })
  .passthrough();

export type FronteggGroupRole = z.infer<typeof fronteggGroupRoleSchema>;

/**
 * Raw row from GET …/identity/resources/groups/v1 (also accepts legacy `vendorId` / `key`).
 * Extra keys are preserved via passthrough on the normalized {@link FronteggGroup}.
 */
const fronteggGroupRowSchema = z
  .object({
    id: z.string().optional(),
    vendorId: z.string().optional(),
    name: z.string().optional(),
    key: z.string().optional(),
    color: z.string().optional(),
    description: z.union([z.string(), z.null()]).optional(),
    metadata: z.unknown().nullable().optional(),
    roles: z
      .array(z.unknown())
      .optional()
      .transform((roles) => {
        if (!roles) {
          return undefined;
        }
        return roles.flatMap((role) => {
          const parsed = fronteggGroupRoleSchema.safeParse(role);
          return parsed.success ? [parsed.data] : [];
        });
      }),
    users: z
      .array(z.unknown())
      .optional()
      .transform((users) => {
        if (!users) {
          return undefined;
        }
        return users.flatMap((user) => {
          const parsed = fronteggGroupUserSchema.safeParse(user);
          return parsed.success ? [parsed.data] : [];
        });
      }),
    managedBy: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

// rows stay unknown here, the loop below parses them one by one so a single
// odd group drops that row instead of the whole list
const fronteggGroupsPayloadSchema = z.union([
  z.array(z.unknown()),
  z
    .object({
      groups: z.array(z.unknown()).optional(),
      items: z.array(z.unknown()).optional(),
      data: z.array(z.unknown()).optional(),
    })
    .passthrough(),
]);

type FronteggGroupRow = z.infer<typeof fronteggGroupRowSchema>;

export type FronteggGroup = Omit<
  FronteggGroupRow,
  "id" | "name" | "vendorId" | "key"
> & {
  id: string;
  name: string;
};

/** @deprecated Prefer {@link FronteggGroup} — same shape; kept for existing imports. */
export type FronteggGroupOption = FronteggGroup;

export function parseFronteggGroupsPayload(json: unknown): FronteggGroup[] {
  const parsed = fronteggGroupsPayloadSchema.safeParse(json);
  if (!parsed.success) {
    log("frontegg_groups_payload_unreadable", {
      level: "warning",
      track: true,
      reason: "response was neither a list nor a groups envelope",
    });
    return [];
  }
  const root = parsed.data;
  const list = Array.isArray(root)
    ? root
    : (root.groups ?? root.items ?? root.data ?? []);

  const result: FronteggGroup[] = [];
  for (const item of list) {
    const row = fronteggGroupRowSchema.safeParse(item);
    if (!row.success) {
      continue;
    }
    const r = row.data;
    /*
      The id must be the GROUP's id and nothing else: it is round-tripped into a
      module's `accessible_groups`, which the backend validates against Frontegg
      group ids and rejects with INVALID_GROUP_IDS otherwise.

      This used to fall back to `vendorId`, which is the vendor/application id —
      the same value on every group in the tenant. So if the fallback ever fired
      it collapsed every chip to one duplicate id AND made the save fail. A row
      we cannot identify is better dropped than rendered as a chip that cannot
      be saved. `key` is still a fine fallback for the display NAME below, which
      is never sent anywhere.
    */
    const id = r.id;
    if (!id) {
      continue;
    }
    const name =
      r.name && r.name.trim() ? r.name : r.key && r.key.trim() ? r.key : id;
    const { vendorId, key, ...rest } = r;
    void vendorId;
    void key;
    result.push({
      ...rest,
      id,
      name,
    });
  }
  return result;
}

/** Role ids currently assigned to a Frontegg group. */
export function getFronteggGroupRoleIds(group: FronteggGroup): string[] {
  if (!Array.isArray(group.roles)) {
    return [];
  }
  return group.roles.map((role) => role.id);
}

export function getCurrentRoleIdsForGroup(group: FronteggGroup): string[] {
  return getFronteggGroupRoleIds(group);
}

/** User ids currently embedded on a Frontegg group. */
export function getFronteggGroupUserIds(group: FronteggGroup): string[] {
  if (!Array.isArray(group.users)) {
    return [];
  }
  return group.users.map((user) => user.id);
}

export function getCurrentUserIdsForGroup(group: FronteggGroup): string[] {
  return getFronteggGroupUserIds(group);
}

/**
 * Diffs current vs next user IDs so assign/unassign can run as separate calls.
 */
export function diffUserIds(currentIds: string[], nextIds: string[]) {
  const current = new Set(currentIds);
  const next = new Set(nextIds);
  return {
    addUserIds: nextIds.filter((id) => !current.has(id)),
    removeUserIds: currentIds.filter((id) => !next.has(id)),
  };
}

/**
 * Diffs current vs next group IDs for a single user's membership changes.
 */
export function diffGroupIds(currentIds: string[], nextIds: string[]) {
  const { addUserIds, removeUserIds } = diffUserIds(currentIds, nextIds);
  return {
    addGroupIds: addUserIds,
    removeGroupIds: removeUserIds,
  };
}

/**
 * Group ids whose embedded `users` include the given user (from
 * `_groupsRelations=rolesAndUsers` list payloads).
 */
export function getGroupIdsForUser(
  groups: FronteggGroup[],
  userId: string,
): string[] {
  return groups
    .filter((group) => getFronteggGroupUserIds(group).includes(userId))
    .map((group) => group.id);
}
